import AthenaAPI from '@/APIs/AthenaAPI';
import {useCallback} from 'react';
import {useMutation} from '@tanstack/react-query';

export function useUnderwritingAnalytics({applicationId, formId}: {applicationId: string | null; formId: string}) {
    const {mutateAsync: storeQuestionAndAnswerMutation} = useMutation({
        mutationFn: AthenaAPI.storeQuestionAndAnswer,
        onError: (error) => {
            console.error('Failed to add question:', error);
        },
    });

    const {mutateAsync: storeBulkQuestionAndAnswerMutation} = useMutation({
        mutationFn: AthenaAPI.storeBulkQuestionAndAnswer,
        onError: (error) => {
            console.error('Failed to store bulk questions:', error);
        },
    });

    const {mutateAsync: storeDecisionMutation} = useMutation({
        mutationFn: AthenaAPI.storeDecision,
        onError: (error) => {
            console.error('Failed to store decision:', error);
        },
    });

    const storeQuestionAndAnswer = useCallback(
        async ({questionKey, answer, bootstrapped}: {questionKey: string; answer: any; bootstrapped: boolean}) => {
            if (!applicationId || !formId || answer === null) return;
            try {
                await storeQuestionAndAnswerMutation({
                    applicationId,
                    questionKey,
                    answer,
                    bootstrapped,
                    formId,
                });
            } catch (error) {
                console.log('Failed to track question/answer:', error);
                return;
            }
        },
        [applicationId, formId],
    );

    const storeBulkQuestionAnswer = useCallback(
        async (questions: {questionKey: string; answer: any; bootstrapped: boolean}[]) => {
            if (!applicationId || !formId) return;
            try {
                await storeBulkQuestionAndAnswerMutation({
                    applicationId,
                    formId,
                    questions,
                });
            } catch (error) {
                console.log('Failed to track question/answer:', error);
                return;
            }
        },
        [applicationId, formId],
    );

    const storeDecision = useCallback(
        async ({decision}: {decision: 'APPROVED' | 'REFERRED' | 'DECLINED' | 'CANCELLED'}) => {
            if (!applicationId || !formId) return;

            try {
                await storeDecisionMutation({
                    applicationId,
                    decision,
                    formId,
                });
            } catch (error) {
                console.log('Failed to track decision:', error);
                return;
            }
        },
        [applicationId, formId],
    );

    return {storeQuestionAndAnswer, storeBulkQuestionAnswer, storeDecision};
}
